import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Queue } from '@rossum/api-client/queues';
import { SelectionPanelActionDescriptor } from '@rossum/components/selection-panel/createSelectionPanelItem';
import { SelectionPanel as BaseSelectionPanel } from '@rossum/components/selection-panel/SelectionPanel';
import {
  IconRefresh,
  IconSchool,
  IconSchoolOff,
} from '@rossum/ui/icons/tabler';
import { SvgIcon, Typography } from '@rossum/ui/material';
import { GridRowSelectionModel } from '@rossum/ui/x-data-grid-pro';
import { reautomateEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { canUserPurgeSelector } from '../../../redux/modules/user/selectors';
import {
  actionIcons,
  getExportMutationState,
  getSupportedBatchActions,
  panelButtonMeta,
  SelectionPanelButtonAction,
} from '../annotation-actions/config';
import { usePermittedActions } from '../annotation-actions/hooks/usePermittedActions';
import { BatchLabels } from '../components/BatchLabels';
import { ExportMenu } from '../export-menu/ExportMenu';
import { moveToReviewTabs } from '../statuses/tabs';
import { AllDocsAnnotation } from '../types';
import { useAnnotationActionsContext } from './AnnotationActionContext';
import { getNextTrainingEnabledValue } from './hooks/useSetTrainingEnabled';
import { ReProcessMenu } from './ReProcessMenu';

const SELECTION_TEXT_MIN_WIDTH = 90;

type SelectionPanelProps = {
  selectedAnnotations: Array<AllDocsAnnotation>;
  setSelectedAnnotations: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  queues: Queue[] | undefined;
};

const isActionAvailable = (
  action: SelectionPanelButtonAction,
  permittedActions: ReadonlyArray<string>,
  supportedBatchActions: Record<
    SelectionPanelButtonAction,
    boolean | 'disabled'
  >
) => {
  if (!permittedActions.includes(action)) return false;
  return supportedBatchActions[action];
};

const SelectionPanel = ({
  selectedAnnotations,
  setSelectedAnnotations,
  queues = [],
}: SelectionPanelProps) => {
  const intl = useIntl();
  const itemsCount = selectedAnnotations.length;
  const showSelectionPanel = itemsCount > 0;

  const canUserPurge = useSelector(canUserPurgeSelector);

  const { permittedActions, isLabelsEnabled } = usePermittedActions();

  const supportedBatchActions = getSupportedBatchActions(
    selectedAnnotations,
    queues.map(({ url, useConfirmedState }) => ({
      url,
      useConfirmedState,
    })),
    isLabelsEnabled,
    canUserPurge
  );

  const {
    annotationActions: actions,
    activeTabKey,
    queueExportSettings,
  } = useAnnotationActionsContext();
  const showMoveToReview =
    activeTabKey !== null && moveToReviewTabs.has(activeTabKey);
  const isReAutomateEnabled = useSelector(reautomateEnabledSelector);

  const handleDeselect = () =>
    setSelectedAnnotations({ type: 'include', ids: new Set() });

  const selectedLabel = (
    <Typography
      variant="body2"
      sx={{ display: 'inline-block', minWidth: SELECTION_TEXT_MIN_WIDTH }}
    >
      {intl.formatMessage(
        { id: 'containers.allDocuments.selectionPanel.selected' },
        { count: itemsCount }
      )}
    </Typography>
  );

  const disabledTooltip = intl.formatMessage({
    id: 'components.annotationOverview.actionButtons.disabled',
  });

  const willEnable = getNextTrainingEnabledValue(selectedAnnotations);
  const hasReExtractAction = isActionAvailable(
    'reExtract',
    permittedActions,
    supportedBatchActions
  );
  const hasReAutomateAction = isActionAvailable(
    'reAutomate',
    permittedActions,
    supportedBatchActions
  );

  const allActions: SelectionPanelActionDescriptor[] = R.pipe(
    [
      // 1. Primary action: Review or To Reviews
      !showMoveToReview &&
        isActionAvailable(
          'validate',
          permittedActions,
          supportedBatchActions
        ) &&
        actions &&
        ({
          id: 'validate',
          label: intl.formatMessage({
            id: panelButtonMeta.validate.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.validate />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-validate-button',
          ...(supportedBatchActions.validate === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.validate({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      showMoveToReview &&
        isActionAvailable(
          'moveToReview',
          permittedActions,
          supportedBatchActions
        ) &&
        actions &&
        ({
          id: 'moveToReview',
          label: intl.formatMessage({
            id: panelButtonMeta.moveToReview.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.moveToReview />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-move-to-review-button',
          ...(supportedBatchActions.moveToReview === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                tooltip: intl.formatMessage(
                  {
                    id: 'components.annotationOverview.actionButtons.moveToReview.tooltip',
                  },
                  {
                    bold: (chunks: React.ReactNode) => (
                      <strong>{chunks}</strong>
                    ),
                  }
                ),
                onClick: () =>
                  actions.moveToReview({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 2. Postpone
      isActionAvailable('postpone', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'postpone',
          label: intl.formatMessage({
            id: panelButtonMeta.postpone.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.postpone />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-postpone-button',
          ...(supportedBatchActions.postpone === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.postpone({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 3. Label
      isActionAvailable('label', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'label',
          label: intl.formatMessage({ id: panelButtonMeta.label.localization }),
          icon: (
            <SvgIcon>
              <actionIcons.label />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-label-button',
          ...(supportedBatchActions.label === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                renderSubMenu: ({ renderTrigger, nested, onCloseParent }) => (
                  <BatchLabels
                    selectedAnnotations={selectedAnnotations}
                    onLabel={actions.label}
                    nested={nested}
                    onParentClose={onCloseParent}
                  >
                    {renderTrigger}
                  </BatchLabels>
                ),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 4. Move
      isActionAvailable('move', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'move',
          label: intl.formatMessage({ id: panelButtonMeta.move.localization }),
          icon: (
            <SvgIcon>
              <actionIcons.move />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-move-button',
          ...(supportedBatchActions.move === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.move({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 5. Export
      isActionAvailable('export', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'export',
          label: intl.formatMessage({
            id: panelButtonMeta.export.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.export />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-export-button',
          ...(supportedBatchActions.export === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                renderSubMenu: ({ renderTrigger, nested, onCloseParent }) => {
                  const exportMutationState = getExportMutationState(
                    selectedAnnotations,
                    queueExportSettings
                  );
                  const annotationIds = selectedAnnotations.map(({ id }) => id);

                  return (
                    <ExportMenu
                      exportMutationState={exportMutationState}
                      onParentClose={onCloseParent}
                      onDownload={format =>
                        actions.download({
                          selectedAnnotationsIds: annotationIds,
                          format,
                        })
                      }
                      onExport={
                        exportMutationState.visible
                          ? format =>
                              actions.export({
                                selectedAnnotationsIds: annotationIds,
                                format,
                              })
                          : undefined
                      }
                      onAsyncDownload={() =>
                        actions.asyncDownload({
                          annotations: selectedAnnotations,
                        })
                      }
                      selectedItemsCount={selectedAnnotations.length}
                      nested={nested}
                    >
                      {renderTrigger}
                    </ExportMenu>
                  );
                },
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 6. Training
      permittedActions.includes('toggleTraining') &&
        actions &&
        ({
          id: 'toggleTraining',
          label: intl.formatMessage({
            id: willEnable
              ? 'containers.annotationList.selectionPanel.buttons.includeInTraining'
              : 'containers.annotationList.selectionPanel.buttons.excludeFromTraining',
          }),
          icon: (
            <SvgIcon>{willEnable ? <IconSchool /> : <IconSchoolOff />}</SvgIcon>
          ),
          tooltip: intl.formatMessage({
            id: 'containers.annotationList.selectionPanel.buttons.trainingToggle.tooltip',
          }),
          'data-cy': 'all-documents-selection-panel-toggle-training-button',
          onClick: () =>
            actions.toggleTraining({ annotations: selectedAnnotations }),
        } satisfies SelectionPanelActionDescriptor),

      // 7. Reprocess / Re-process confirmed
      isActionAvailable('reprocess', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'reprocess',
          label: intl.formatMessage({
            id: panelButtonMeta.reprocess.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.reprocess />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-reprocess-button',
          ...(supportedBatchActions.reprocess === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.reprocess({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      isActionAvailable(
        'reprocessConfirmed',
        permittedActions,
        supportedBatchActions
      ) &&
        actions &&
        ({
          id: 'reprocessConfirmed',
          label: intl.formatMessage({
            id: panelButtonMeta.reprocessConfirmed.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.reprocessConfirmed />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-reprocessConfirmed-button',
          ...(supportedBatchActions.reprocessConfirmed === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.reprocessConfirmed({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 8. Re-extract
      isActionAvailable('reExtract', permittedActions, supportedBatchActions) &&
        actions &&
        (isReAutomateEnabled
          ? ({
              id: 'reProcess',
              label: intl.formatMessage({
                id: 'containers.annotationList.selectionPanel.buttons.reProcess',
              }),
              icon: (
                <SvgIcon>
                  <IconRefresh />
                </SvgIcon>
              ),
              'data-cy': 'all-documents-selection-panel-reProcess-button',
              renderSubMenu: ({ renderTrigger, nested, onCloseParent }) => (
                <ReProcessMenu
                  onReExtract={
                    hasReExtractAction
                      ? () =>
                          actions.reExtract({
                            annotations: selectedAnnotations,
                            onSuccess: handleDeselect,
                          })
                      : undefined
                  }
                  onReAutomate={
                    hasReAutomateAction
                      ? () =>
                          actions.reAutomate({
                            annotations: selectedAnnotations,
                            onSuccess: handleDeselect,
                          })
                      : undefined
                  }
                  nested={nested}
                  onParentClose={onCloseParent}
                >
                  {renderTrigger}
                </ReProcessMenu>
              ),
            } satisfies SelectionPanelActionDescriptor)
          : hasReExtractAction
            ? ({
                id: 'reExtract',
                label: intl.formatMessage({
                  id: panelButtonMeta.reExtract.localization,
                }),
                icon: (
                  <SvgIcon>
                    <actionIcons.reExtract />
                  </SvgIcon>
                ),
                'data-cy': 'all-documents-selection-panel-reExtract-button',
                ...(supportedBatchActions.reExtract === 'disabled'
                  ? { disabled: true, tooltip: disabledTooltip }
                  : {
                      onClick: () =>
                        actions.reExtract({
                          annotations: selectedAnnotations,
                          onSuccess: handleDeselect,
                        }),
                    }),
              } satisfies SelectionPanelActionDescriptor)
            : false),

      // 9. Delete
      isActionAvailable('delete', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'delete',
          label: intl.formatMessage({
            id: panelButtonMeta.delete.localization,
          }),
          icon: (
            <SvgIcon>
              <actionIcons.delete />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-delete-button',
          tooltip:
            supportedBatchActions.delete === 'disabled'
              ? disabledTooltip
              : intl.formatMessage(
                  {
                    id: 'containers.annotationList.selectionPanel.buttons.delete.tooltip',
                  },
                  {
                    bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }
                ),
          ...(supportedBatchActions.delete === 'disabled'
            ? { disabled: true }
            : {
                onClick: () =>
                  actions.delete({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),

      // 10. Delete forever (purge)
      isActionAvailable('purge', permittedActions, supportedBatchActions) &&
        actions &&
        ({
          id: 'purge',
          label: intl.formatMessage({ id: panelButtonMeta.purge.localization }),
          icon: (
            <SvgIcon>
              <actionIcons.purge />
            </SvgIcon>
          ),
          'data-cy': 'all-documents-selection-panel-purge-button',
          ...(supportedBatchActions.purge === 'disabled'
            ? { disabled: true, tooltip: disabledTooltip }
            : {
                onClick: () =>
                  actions.purge({
                    annotations: selectedAnnotations,
                    onSuccess: handleDeselect,
                  }),
              }),
        } satisfies SelectionPanelActionDescriptor),
    ],
    R.filter(R.isTruthy)
  );

  return (
    <BaseSelectionPanel
      visible={showSelectionPanel}
      selectedLabel={selectedLabel}
      onDeselect={handleDeselect}
      actions={allActions}
      data-cy="all-documents-selection-panel"
    />
  );
};

export { SelectionPanel };
